import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ContactForm from "./ContactForm";
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { LogOut, Settings } from 'lucide-react';

// Services avec leurs bulles
const services = [
  { id: 'acoustique', name: 'Acoustique', video: '/videos/bubbles/bubble1.mp4', active: true, route: '/acoustics' },
  { id: 'meditation', name: 'Méditation', video: '/videos/bubbles/bubble2.mp4', active: true, route: '/meditation' },
  { id: 'bien-etre', name: 'Bien-être', video: '/videos/bubbles/bubble3.mp4', active: false, route: null },
  { id: 'creation', name: 'Création', video: '/videos/bubbles/bubble4.mp4', active: false, route: null },
  { id: 'developpement', name: 'Développement', video: '/videos/bubbles/bubble5.mp4', active: false, route: null },
  { id: 'edition', name: 'Édition', video: '/videos/bubbles/bubble6.mp4', active: false, route: null },
  { id: 'tourisme', name: 'Tourisme', video: '/videos/bubbles/bubble1.mp4', active: true, route: '/tourism' },
  { id: 'mode-vie', name: 'Mode de vie', video: '/videos/bubbles/bubble2.mp4', active: false, route: null },
];

const ServiceBubblesHome = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [hoveredService, setHoveredService] = useState(null);
  const [selectedService, setSelectedService] = useState(null);

  const handleLogout = () => {
    sessionStorage.removeItem('tkale_intro_seen');
    onLogout();
    window.location.href = '/';
  };

  const handleBubbleClick = (service) => {
    if (service.active && service.route) {
      navigate(service.route);
    } else {
      setSelectedService(service);
    }
  };

  // Taille de la bulle centrale (poulpe)
  const centerSize = 420; // px
  // Taille des petites bulles
  const bubbleSize = 130; // px
  // Distance du centre aux petites bulles - PLUS ESPACÉES
  const orbitRadius = 340; // px (était 280)

  // Positions des 8 bulles en cercle parfait
  const getBubblePosition = (index) => {
    const angle = (index * 45 - 90) * (Math.PI / 180); // 45° entre chaque, commencer en haut
    const x = Math.cos(angle) * orbitRadius;
    const y = Math.sin(angle) * orbitRadius;
    return { x, y };
  };

  // Position du label selon la position de la bulle
  // Haut (0) et Bas (4) → en dessous
  // Droite (1, 2, 3) → à droite  
  // Gauche (5, 6, 7) → à gauche
  const getLabelPosition = (index) => {
    if (index === 0) {
      // Haut → label en dessous
      return { bottom: '-45px', left: '50%', transform: 'translateX(-50%)' };
    }
    if (index === 4) {
      // Bas → label en dessous
      return { bottom: '-45px', left: '50%', transform: 'translateX(-50%)' };
    }
    if (index === 1 || index === 2 || index === 3) {
      // Droite → label à droite
      return { left: 'calc(100% + 15px)', top: '50%', transform: 'translateY(-50%)' };
    }
    // Gauche (5, 6, 7) → label à gauche
    return { right: 'calc(100% + 15px)', top: '50%', transform: 'translateY(-50%)' };
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      
      {/* Container centré */}
      <div className="absolute inset-0 flex items-center justify-center">
        
        {/* Poulpe au centre - PLUS GRAND */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="absolute rounded-full overflow-hidden"
          style={{
            width: centerSize,
            height: centerSize,
            boxShadow: '0 0 60px rgba(59, 130, 246, 0.5)',
            border: '3px solid rgba(255,255,255,0.3)',
            zIndex: 5,
          }}
        >
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="/videos/poulpe-background.mp4" type="video/mp4" />
          </video>
        </motion.div>

        {/* 8 Bulles symétriques autour */}
        {services.map((service, index) => {
          const pos = getBubblePosition(index);
          const isHovered = hoveredService?.id === service.id;
          
          return (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
              className="absolute cursor-pointer"
              style={{
                width: bubbleSize,
                height: bubbleSize,
                left: `calc(50% + ${pos.x}px - ${bubbleSize/2}px)`,
                top: `calc(50% + ${pos.y}px - ${bubbleSize/2}px)`,
                zIndex: 10,
              }}
              onMouseEnter={() => setHoveredService(service)}
              onMouseLeave={() => setHoveredService(null)}
              onClick={() => handleBubbleClick(service)}
            >
              {/* Animation de flottement */}
              <motion.div
                animate={{ y: [0, -6, 0, 6, 0] }}
                transition={{ repeat: Infinity, duration: 3 + index * 0.3, ease: "easeInOut" }}
                className="relative w-full h-full"
              >
                {/* Bulle vidéo */}
                <div
                  className="w-full h-full rounded-full overflow-hidden"
                  style={{
                    boxShadow: isHovered 
                      ? '0 0 35px rgba(255,255,255,0.7)' 
                      : '0 0 20px rgba(100, 150, 255, 0.5)',
                    border: isHovered ? '3px solid rgba(255,255,255,0.8)' : '2px solid rgba(255,255,255,0.4)',
                    transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                    transition: 'all 0.3s ease',
                    background: '#1e3a5f',
                  }}
                >
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                    style={{ minWidth: '100%', minHeight: '100%' }}
                  >
                    <source src={service.video} type="video/mp4" />
                  </video>
                </div>

                {/* Label du service - Toujours visible */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="absolute whitespace-nowrap"
                  style={getLabelPosition(index)}
                >
                  <div className={`px-3 py-1.5 rounded-full font-semibold text-sm shadow-lg transition-all ${
                    service.active 
                      ? 'bg-white/90 text-slate-800' 
                      : 'bg-slate-700/80 text-slate-400'
                  } ${isHovered ? 'scale-110' : 'scale-100'}`}>
                    {service.name}
                    {!service.active && <span className="ml-1 text-xs">(bientôt)</span>}
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          );
        })}
      </div>

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 p-6" style={{ zIndex: 20 }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-4xl font-bold text-white mb-2 whitespace-nowrap">T-Kalé Platform</h1>
            <p className="text-blue-200">Bienvenue {user?.username || user?.email}</p>
          </div>
          <div className="flex items-center space-x-4">
            {user?.role === 'admin' && (
              <Button
                onClick={() => navigate('/admin/dashboard')}
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              >
                <Settings className="w-4 h-4 mr-2" />
                Admin Dashboard
              </Button>
            )}
            <Button
              onClick={handleLogout}
              variant="outline"
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </div>

      {/* Service non disponible Modal */}
      <AnimatePresence>
        {selectedService && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
            onClick={() => setSelectedService(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-slate-800/90 backdrop-blur-lg rounded-2xl p-8 max-w-md mx-4 border border-white/20"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <h3 className="text-2xl font-bold text-white mb-2">{selectedService.name}</h3>
                <p className="text-blue-200 mb-6">
                  Ce service sera bientôt disponible ! Le poulpe y travaille... 🐙
                </p>
                <Button 
                  onClick={() => setSelectedService(null)}
                  className="w-full bg-blue-600 hover:bg-blue-500"
                >
                  Compris !
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ServiceBubblesHome;
